import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Megaphone, 
  Plus, 
  Mail,
  Calendar,
  MoreVertical,
  Eye,
  Copy,
  Trash2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { mauticApi } from '../lib/api';

const statusColors = {
  completed: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-blue-100 text-blue-700',
  draft: 'bg-slate-100 text-slate-700',
  sending: 'bg-amber-100 text-amber-700',
};

export const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await mauticApi.getCampaigns();
        setCampaigns(response.data.campaigns);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  return (
    <div className="p-8 animate-fade-in" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Campañas</h1>
          <p className="text-slate-500 mt-1">Gestiona tus campañas de email marketing</p>
        </div>
        <Link to="/campaigns/new">
          <Button data-testid="new-campaign-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Campaña
          </Button>
        </Link>
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-6 bg-slate-100 rounded animate-pulse w-1/3 mb-2" />
                <div className="h-4 bg-slate-100 rounded animate-pulse w-1/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : campaigns.length > 0 ? (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} className="card-hover" data-testid={`campaign-${campaign.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      campaign.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-100 text-indigo-600'
                    }`}>
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">{campaign.name}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {campaign.date}
                        </span>
                        <Badge className={statusColors[campaign.status] || statusColors.draft}>
                          {campaign.status}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{campaign.sent?.toLocaleString() || 0}</p>
                      <p className="text-xs text-slate-500">Enviados</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{campaign.opened?.toLocaleString() || 0}</p>
                      <p className="text-xs text-slate-500">Abiertos</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {campaign.sent > 0 ? Math.round((campaign.opened / campaign.sent) * 100) : 0}%
                      </p>
                      <p className="text-xs text-slate-500">Apertura</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-indigo-600">{campaign.clicked?.toLocaleString() || 0}</p>
                      <p className="text-xs text-slate-500">Clicks</p>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`campaign-menu-${campaign.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver detalles
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicar
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <Megaphone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay campañas</h3>
          <p className="text-slate-500 mb-4">Crea tu primera campaña de email marketing</p>
          <Link to="/campaigns/new">
            <Button data-testid="create-first-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />
              Crear campaña
            </Button>
          </Link>
        </Card>
      )}
    </div>
  );
};

export default Campaigns;
